import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Download, FileArchive, Loader2, ShieldAlert } from "lucide-react";
import JSZip from "jszip";

type Manifest = Record<string, number>;

export default function DataExport() {
  const { toast } = useToast();
  const [loadingJson, setLoadingJson] = useState(false);
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [exportedAt, setExportedAt] = useState<string>("");

  async function fetchExport(): Promise<any> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not signed in");

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/export-platform-data`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
        "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      },
      body: JSON.stringify({}),
    });

    if (res.status === 403) throw new Error("Forbidden: admin access required");
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Export failed (${res.status}): ${text.slice(0, 200)}`);
    }
    return await res.json();
  }

  function downloadBlob(blob: Blob, filename: string) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function todayStamp() {
    return new Date().toISOString().slice(0, 10);
  }

  async function handleJsonExport() {
    setLoadingJson(true);
    try {
      const payload = await fetchExport();
      setManifest(payload.manifest || {});
      setTotalRows(payload.total_rows || 0);
      setExportedAt(payload.exported_at || "");
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      downloadBlob(blob, `wealthy-steward-export-${todayStamp()}.json`);
      toast({ title: "Export complete", description: `${payload.total_rows} rows across ${Object.keys(payload.manifest || {}).length} tables.` });
    } catch (e: any) {
      toast({ title: "Export failed", description: e.message, variant: "destructive" });
    } finally {
      setLoadingJson(false);
    }
  }

  function rowsToCsv(rows: any[]): string {
    if (!rows || rows.length === 0) return "";
    const cols = Array.from(
      rows.reduce((set, r) => {
        Object.keys(r || {}).forEach((k) => set.add(k));
        return set;
      }, new Set<string>())
    ) as string[];
    const esc = (v: any) => {
      if (v === null || v === undefined) return "";
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const header = cols.join(",");
    const body = rows.map((r) => cols.map((c) => esc(r[c])).join(",")).join("\n");
    return `${header}\n${body}`;
  }

  async function handleCsvZipExport() {
    setLoadingCsv(true);
    try {
      const payload = await fetchExport();
      setManifest(payload.manifest || {});
      setTotalRows(payload.total_rows || 0);
      setExportedAt(payload.exported_at || "");

      const zip = new JSZip();
      for (const [table, rows] of Object.entries<any>(payload.tables || {})) {
        zip.file(`${table}.csv`, rowsToCsv(rows as any[]));
      }
      zip.file(
        "manifest.json",
        JSON.stringify({ exported_at: payload.exported_at, manifest: payload.manifest, total_rows: payload.total_rows }, null, 2)
      );
      const blob = await zip.generateAsync({ type: "blob" });
      downloadBlob(blob, `wealthy-steward-export-${todayStamp()}.zip`);
      toast({ title: "CSV bundle ready", description: `${Object.keys(payload.tables || {}).length} tables zipped.` });
    } catch (e: any) {
      toast({ title: "Export failed", description: e.message, variant: "destructive" });
    } finally {
      setLoadingCsv(false);
    }
  }

  const busy = loadingJson || loadingCsv;

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Data Export</h1>
        <p className="text-muted-foreground text-sm sm:text-base">
          Download a full backup of production data.
        </p>
      </div>

      <Alert variant="destructive">
        <ShieldAlert className="h-4 w-4" />
        <AlertDescription>
          This file contains all member data. Store it securely, do not email it, and delete local copies when they are no longer needed.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Full backup</CardTitle>
          <CardDescription>
            Exports every non regenerable table. Large exports may take a minute or two.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={handleJsonExport} disabled={busy} className="min-h-[44px]">
              {loadingJson ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Download JSON backup
            </Button>
            <Button onClick={handleCsvZipExport} disabled={busy} variant="outline" className="min-h-[44px]">
              {loadingCsv ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileArchive className="h-4 w-4 mr-2" />}
              Download CSV bundle (ZIP)
            </Button>
          </div>
        </CardContent>
      </Card>

      {manifest && (
        <Card>
          <CardHeader>
            <CardTitle>Last export manifest</CardTitle>
            <CardDescription>
              Exported at {exportedAt}. Total rows: {totalRows.toLocaleString()}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Table</th>
                    <th className="text-right p-2 font-medium">Rows</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(manifest)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([table, count]) => (
                      <tr key={table} className="border-t">
                        <td className="p-2 font-mono">{table}</td>
                        <td className="p-2 text-right">{count.toLocaleString()}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
